import React, { useCallback, useMemo } from "react";
import { Base } from "../../../framework/base";
import { SalaryRowTypeItem } from "../../../models/salary/salaryRowTypeItem";
import { WorkTimeDetailsTable } from "./workTimeDetailsTable";
import { useTranslation } from "react-i18next";
import { GroupingValue } from "../../../store/reportOptionsSlice";
import { formatTime } from "../../salaries/salariesListGrid";
import { WorkTimeReportGridRow } from "../../../models/reporting/workTimeReport";
import {
    GroupableDataGrid,
    GroupableDataGridColDef,
} from "../../framework/groupedDataGrid/groupableDataGrid";
import { EmployeeGroupCalculation } from "../../../hooks/reporting/useWorkTimeReportData";
import { SortModel } from "../../../models/reporting/reportTemplate";

interface GroupableWorkTimeGridProps {
    salaryRowTypes: SalaryRowTypeItem[];
    workTimeReportData: WorkTimeReportGridRow[];
    columns: GroupableDataGridColDef<WorkTimeReportGridRow>[];
    groupingModel: (keyof WorkTimeReportGridRow)[];
    pinnedColumns?: string[];
    employeeGroupCalculations: EmployeeGroupCalculation[];
    grouping: string[];
    groupingModeMultiple: boolean;
    showGroupedRows: boolean;
    selectedColumns: string[];
    onColumnOrderChange: (val: string[]) => void;
    sortModel: SortModel[];
    onSortModelChange: (val: SortModel[]) => void;
    tableMaxHeight?: string;
}

export const GroupableWorkTimeGrid = ({
    salaryRowTypes,
    workTimeReportData,
    columns,
    groupingModel,
    pinnedColumns,
    employeeGroupCalculations,
    grouping,
    groupingModeMultiple,
    showGroupedRows,
    selectedColumns,
    onColumnOrderChange,
    sortModel,
    onSortModelChange,
    tableMaxHeight,
}: GroupableWorkTimeGridProps) => {
    const { t } = useTranslation();

    const hiddenColumns = useMemo(
        () => [
            "id",
            "workShiftId",
            "warning",
            ...columns
                .filter(
                    (c) =>
                        !pinnedColumns.includes(c.field) &&
                        !selectedColumns.includes(c.field) &&
                        !groupingModel.includes(
                            c.field as keyof WorkTimeReportGridRow
                        )
                )
                .map((c) => c.field),
        ],
        [columns, selectedColumns, pinnedColumns, groupingModel]
    );

    const getGroupingColLabel = useCallback(
        (value: any, field: string) => {
            if (field === "id") {
                const wh = workTimeReportData?.find((w) => w.id === value);

                if (!!wh) {
                    const showDate = !grouping.includes(GroupingValue.Date);

                    return [
                        showDate
                            ? Base.dayjsToDateStrWithWeekday(wh.dateStr)
                            : null,
                        !!wh.startTime
                            ? `${formatTime(
                                  wh.startTime,
                                  wh.dateStr
                              )} - ${formatTime(wh.endTime, wh.dateStr)}`
                            : wh.salaryRows.find((sr) => !!sr.salaryRowTypeName)
                                ?.salaryRowTypeName,
                    ]
                        .filter(Boolean)
                        .join(" ");
                }
            } else if (field === "workShiftId") {
                const wh = workTimeReportData?.find(
                    (w) => w.workShiftId === value
                );

                if (!!wh || !value) {
                    const showDate = !grouping.includes(GroupingValue.Date);

                    return [
                        showDate && !!wh
                            ? Base.dayjsToDateStrWithWeekday(wh.dateStr)
                            : null,
                        !!wh?.workShiftStartTime
                            ? `${formatTime(
                                  wh.workShiftStartTime,
                                  wh.dateStr
                              )} - ${formatTime(
                                  wh.workShiftEndTime,
                                  wh.dateStr
                              )}`
                            : "-",
                    ]
                        .filter(Boolean)
                        .join(" ");
                }
            } else if (field === "dateStr") {
                if (!!value) {
                    return Base.dayjsToDateStrWithWeekday(value as string);
                }
            } else if (!value) {
                if (field === "vehicleName") {
                    return t("reporting.noFleet");
                } else if (field === "costCenterName") {
                    return t("reporting.noCostCenter");
                } else if (field === "workTimeTypeName") {
                    return t("reporting.noWorkTimeType");
                } else if (field === "customerName") {
                    return t("reporting.noCustomer");
                }
            }

            return value as string;
        },
        [grouping, workTimeReportData, t]
    );

    return (
        <GroupableDataGrid
            noRowsText={
                employeeGroupCalculations?.length > 0
                    ? t("reporting.missingWorkTimeData")
                    : t("reporting.noWorkTimeData")
            }
            noRowsSeverity={
                employeeGroupCalculations?.length > 0 ? "warning" : "info"
            }
            detailsComponent={WorkTimeDetailsTable}
            detailsComponentProps={{ salaryRowTypes }}
            columns={columns}
            rows={workTimeReportData}
            groupingColDef={{
                headerName: t("reporting.groupBy"),
            }}
            getGroupLabel={getGroupingColLabel}
            pinnedColumns={pinnedColumns}
            hiddenColumns={hiddenColumns}
            groupBy={groupingModel}
            groupingModeMultiple={groupingModeMultiple}
            showGroupedRows={showGroupedRows}
            onColumnOrderChange={onColumnOrderChange}
            sortModel={sortModel}
            onSortModelChange={onSortModelChange}
            useExcelPostProcess
            tableMaxHeight={tableMaxHeight}
        />
    );
};
